import React, { useState } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import { Button } from '../../../components/ui';
import { useTranslation } from 'react-i18next';
import { CommentCard } from './CommentCard';
import { hasPermission } from '../../../utils/rbac';
import './CommentsThread.css';

const MODULES = [
  { id: 'm0', nameKey: 'reports.workspace.modules.m0.name' },
  { id: 'm1', nameKey: 'reports.workspace.modules.m1.name' },
  { id: 'm2', nameKey: 'reports.workspace.modules.m2.name' },
  { id: 'm3', nameKey: 'reports.workspace.modules.m3.name' },
  { id: 'm4', nameKey: 'reports.workspace.modules.m4.name' },
];

export function CommentsThread({ 
  comments = [], 
  onAddComment, 
  onReply,
  onEdit,
  onDelete,
  onResolve,
  currentUser,
  selectedModule = 'all'
}) {
  const { t } = useTranslation();
  const [newComment, setNewComment] = useState('');
  const [newCommentModule, setNewCommentModule] = useState('m0');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const canAddComment = hasPermission(currentUser, 'ADD_COMMENT');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim() && onAddComment) {
      const moduleId = selectedModule !== 'all' ? selectedModule : newCommentModule;
      onAddComment(newComment.trim(), moduleId);
      setNewComment('');
      setNewCommentModule('m0');
    }
  };

  const handleReply = (commentId) => {
    setReplyingTo(commentId);
  };

  const handleSubmitReply = (commentId) => {
    if (replyText.trim() && onReply) {
      onReply(commentId, replyText.trim());
      setReplyingTo(null);
      setReplyText('');
    }
  };

  const handleEdit = (commentId, newText) => {
    if (onEdit) {
      onEdit(commentId, newText);
    }
  };

  const handleDelete = (commentId) => {
    if (onDelete) {
      onDelete(commentId);
    }
  };

  const handleResolve = (commentId) => {
    if (onResolve) {
      onResolve(commentId);
    }
  };

  // Flatten comments with nested replies for display
  const renderComments = (commentList) => {
    return commentList.map((comment) => (
      <div key={comment.id}>
        <CommentCard
          comment={comment}
          currentUser={currentUser}
          onReply={handleReply}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onResolve={handleResolve}
          level={0}
        />
        {replyingTo === comment.id && (
          <div className="comments-thread__reply-form">
            <textarea
              className="comments-thread__reply-textarea"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('reports.workspace.comments.replyPlaceholder')}
              rows={3}
            />
            <div className="comments-thread__reply-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setReplyingTo(null);
                  setReplyText('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleSubmitReply(comment.id)}
                disabled={!replyText.trim()}
              >
                <Send size={16} />
                {t('reports.workspace.comments.reply')}
              </Button>
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="comments-thread">
      {/* Comments List */}
      <div className="comments-thread__list">
        {comments.length === 0 ? (
          <div className="comments-thread__empty">
            <MessageSquare size={48} className="comments-thread__empty-icon" />
            <h3 className="comments-thread__empty-title">{t('reports.workspace.comments.empty.title')}</h3>
            <p className="comments-thread__empty-message">{t('reports.workspace.comments.empty.message')}</p>
          </div>
        ) : (
          renderComments(comments)
        )}
      </div>

      {/* Add Comment */}
      {canAddComment && (
        <div className="comments-thread__add-comment">
          <textarea
            className="comments-thread__textarea"
            placeholder={t('reports.workspace.comments.addPlaceholder')}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            rows={4}
          />
          {selectedModule === 'all' && (
            <select
              className="comments-thread__module-select"
              value={newCommentModule}
              onChange={(e) => setNewCommentModule(e.target.value)}
            >
              {MODULES.map((module) => (
                <option key={module.id} value={module.id}>
                  {t('reports.workspace.moduleLabelShort', {
                    id: module.id.toUpperCase(),
                    name: t(module.nameKey),
                  })}
                </option>
              ))}
            </select>
          )}
          <Button
            type="button"
            variant="default"
            onClick={handleSubmit}
            disabled={!newComment.trim()}
            className="comments-thread__submit-btn"
          >
            <Send size={16} />
            {t('reports.workspace.comments.add')}
          </Button>
        </div>
      )}
    </div>
  );
}
